/**
 * Waveform peak storage.
 *
 * Peaks are held as interleaved Int16 min/max pairs — the same representation
 * the server sends (ANALYSIS_API_SPEC.md §1), so local and remote peaks are the
 * same type and the renderer can't tell them apart.
 *
 * The previous representation was an array of `{min, max}` objects, roughly
 * 50-80 bytes per peak against 4. At the durations this library now targets
 * that difference is the whole ballgame: an 8-hour file's 1/100 level is 55 MB
 * as Int16 and well over a gigabyte as objects.
 */

/** Full-scale value for the Int16 encoding. Must match the server exactly. */
export const PEAK_SCALE = 32767;

/** Encode a float sample to the Int16 peak representation, clamping to ±1. */
export function encodePeak(value) {
  const clamped = value < -1 ? -1 : value > 1 ? 1 : value;
  return Math.round(clamped * PEAK_SCALE);
}

/**
 * One mipmap level.
 *
 * Data is held in fixed-size blocks so a level can be sparse. A remote level is
 * only ever partly resident — the client fetches the few thousand peaks the
 * viewport needs, never the 13.8 million an 8-hour file has at 1/100.
 */
export class PeakLevel {
  /**
   * @param {number} samplesPerPeak
   * @param {number} count - total peaks at this level across the whole asset
   * @param {number} [blockPeaks] - peaks per block; defaults to a single block
   */
  constructor(samplesPerPeak, count, blockPeaks = count) {
    this.samplesPerPeak = samplesPerPeak;
    this.count = count;
    this.blockPeaks = Math.max(1, blockPeaks);
    this.blockCount = Math.ceil(count / this.blockPeaks);
    this.blocks = new Map(); // blockIndex -> Int16Array of interleaved min,max
  }

  /** @param {Int16Array} data - 2 entries per peak */
  setBlock(index, data) {
    this.blocks.set(index, data);
  }

  hasBlock(index) {
    return this.blocks.has(index);
  }

  /** Inclusive block index range covering [startPeak, endPeak). */
  blockRange(startPeak, endPeak) {
    const first = Math.max(0, Math.floor(startPeak / this.blockPeaks));
    const last = Math.min(this.blockCount - 1, Math.floor((endPeak - 1) / this.blockPeaks));
    return [first, last];
  }

  missingBlocks(startPeak, endPeak) {
    if (endPeak <= startPeak || this.blockCount === 0) return [];
    const [first, last] = this.blockRange(startPeak, endPeak);
    const missing = [];
    for (let block = first; block <= last; block++) {
      if (!this.blocks.has(block)) missing.push(block);
    }
    return missing;
  }

  /** Whether every block covering the range has arrived. */
  isResident(startPeak, endPeak) {
    const clampedEnd = Math.min(endPeak, this.count);
    if (clampedEnd <= startPeak) return true;
    return this.missingBlocks(Math.max(0, startPeak), clampedEnd).length === 0;
  }

  get residentBytes() {
    let bytes = 0;
    for (const block of this.blocks.values()) bytes += block.byteLength;
    return bytes;
  }

  /**
   * Iterate the resident spans of [startPeak, endPeak) block by block.
   *
   * The draw loop runs once per peak — up to ~10k per frame — so it can't afford
   * a Map lookup per peak. Yielding contiguous typed-array spans keeps the inner
   * loop to plain indexed reads.
   *
   * @yields {{data: Int16Array, firstPeak: number, count: number, offset: number}}
   */
  *chunks(startPeak, endPeak) {
    const from = Math.max(0, startPeak);
    const to = Math.min(this.count, endPeak);
    if (to <= from) return;

    const [firstBlock, lastBlock] = this.blockRange(from, to);
    for (let block = firstBlock; block <= lastBlock; block++) {
      const data = this.blocks.get(block);
      if (!data) continue;

      const blockStart = block * this.blockPeaks;
      const peakFrom = Math.max(from, blockStart);
      const peakTo = Math.min(to, blockStart + this.blockPeaks);
      if (peakTo <= peakFrom) continue;

      yield {
        data,
        firstPeak: peakFrom,
        count: peakTo - peakFrom,
        offset: (peakFrom - blockStart) * 2,
      };
    }
  }
}

/**
 * Pick mipmap levels for an asset: decades from 1/100 up to whatever covers the
 * whole thing in about one screen of peaks. Mirrors the server's rule so a local
 * file and a remote one behave identically.
 *
 * 1/100 is the finest level worth having — at maximum zoom (500 px/s) and
 * 48 kHz there are 96 samples per pixel.
 *
 * @returns {number[]} samples-per-peak, ascending (finest first)
 */
export function chooseLevels(sampleCount) {
  const levels = [];
  for (let samplesPerPeak = 100; ; samplesPerPeak *= 10) {
    levels.push(samplesPerPeak);
    if (Math.ceil(sampleCount / samplesPerPeak) <= 2048) break;
    if (samplesPerPeak > 1e9) break; // paranoia
  }
  return levels;
}
